import { Codicon } from 'vscode/vscode/vs/base/common/codicons';
import { localize2WithPath } from 'vscode/vscode/vs/nls';
import { ContextKeyExpr } from 'vscode/vscode/vs/platform/contextkey/common/contextkey';
import { AbstractInlineChatAction } from 'vscode/vscode/vs/workbench/contrib/inlineChat/browser/inlineChatActions';
import { CTX_INLINE_CHAT_HAS_PROVIDER, CTX_INLINE_CHAT_FOCUSED, CTX_INLINE_CHAT_EMPTY } from 'vscode/vscode/vs/workbench/contrib/inlineChat/common/inlineChat';
import { isDetachedTerminalInstance } from 'vscode/vscode/vs/workbench/contrib/terminal/browser/terminal';
import { registerActiveXtermAction } from '../../../terminal/browser/terminalActions.js';
import { TerminalContextKeys } from 'vscode/vscode/vs/workbench/contrib/terminal/common/terminalContextKey';
import { TerminalChatContextKeys, MENU_TERMINAL_CHAT_WIDGET, MENU_TERMINAL_CHAT_WIDGET_STATUS, MENU_TERMINAL_CHAT_INPUT, MENU_TERMINAL_CHAT_WIDGET_FEEDBACK } from './terminalChat.js';
import { TerminalChatController } from './terminalChatController.js';

const _moduleId = "vs/workbench/contrib/terminalContrib/chat/browser/terminalChatActions";
registerActiveXtermAction({
    id: "workbench.action.terminal.chat.start" ,
    title: ( localize2WithPath(_moduleId, 0, 'Start in Terminal')),
    keybinding: {
        primary: 2048  | 39 ,
        when: ( (ContextKeyExpr.and(TerminalContextKeys.focusInAny))),
        weight: 400  + 1,
    },
    f1: true,
    category: AbstractInlineChatAction.category,
    precondition: ( (ContextKeyExpr.and( (ContextKeyExpr.or(
        TerminalContextKeys.processSupported,
        TerminalContextKeys.terminalHasBeenCreated
    )),
    CTX_INLINE_CHAT_HAS_PROVIDER))),
    run: (_xterm, _accessor, activeInstance, opts) => {
        if (isDetachedTerminalInstance(activeInstance)) {
            return;
        }
        const contr = TerminalChatController.activeChatWidget || TerminalChatController.get(activeInstance);
        if (opts) {
            opts = typeof opts === 'string' ? { query: opts } : opts;
            if (typeof opts === 'object' && opts !== null && 'query' in opts && typeof opts.query === 'string') {
                contr?.updateInput(opts.query, false);
                if (!('isPartialQuery' in opts && opts.isPartialQuery)) {
                    contr?.acceptInput();
                }
            }
        }
        contr?.reveal();
    }
});
registerActiveXtermAction({
    id: "workbench.action.terminal.chat.close" ,
    title: ( localize2WithPath(_moduleId, 1, 'Close Chat')),
    keybinding: {
        primary: 9 ,
        secondary: [1024  | 9 ],
        when: ( (ContextKeyExpr.and(TerminalChatContextKeys.focused, TerminalChatContextKeys.visible))),
        weight: 200 ,
    },
    icon: Codicon.close,
    menu: {
        id: MENU_TERMINAL_CHAT_WIDGET,
        group: 'navigation',
        order: 2
    },
    f1: true,
    precondition: ( (ContextKeyExpr.and(
         (ContextKeyExpr.and(TerminalChatContextKeys.focused, TerminalChatContextKeys.visible))
    ))),
    run: (_xterm, _accessor, activeInstance) => {
        if (isDetachedTerminalInstance(activeInstance)) {
            return;
        }
        const contr = TerminalChatController.activeChatWidget || TerminalChatController.get(activeInstance);
        contr?.clear();
    }
});
registerActiveXtermAction({
    id: "workbench.action.terminal.chat.focusResponse" ,
    title: ( localize2WithPath(_moduleId, 2, 'Focus Terminal Response')),
    keybinding: {
        primary: 2048  | 18 ,
        when: TerminalChatContextKeys.focused,
        weight: 200 ,
    },
    f1: true,
    category: AbstractInlineChatAction.category,
    precondition: ( (ContextKeyExpr.and(TerminalChatContextKeys.focused))),
    run: (_xterm, _accessor, activeInstance) => {
        if (isDetachedTerminalInstance(activeInstance)) {
            return;
        }
        const contr = TerminalChatController.activeChatWidget || TerminalChatController.get(activeInstance);
        contr?.chatWidget?.inlineChatWidget.chatWidget.focusLastMessage();
    }
});
registerActiveXtermAction({
    id: "workbench.action.terminal.chat.focusInput" ,
    title: ( localize2WithPath(_moduleId, 3, 'Focus Terminal Input')),
    keybinding: {
        primary: 2048  | 16 ,
        secondary: [2048  | 39 ],
        when: ( (ContextKeyExpr.and(
            TerminalChatContextKeys.focused,
             (CTX_INLINE_CHAT_FOCUSED.toNegated())
        ))),
        weight: 200 ,
    },
    f1: true,
    category: AbstractInlineChatAction.category,
    precondition: ( (ContextKeyExpr.and(TerminalChatContextKeys.focused))),
    run: (_xterm, _accessor, activeInstance) => {
        if (isDetachedTerminalInstance(activeInstance)) {
            return;
        }
        const contr = TerminalChatController.activeChatWidget || TerminalChatController.get(activeInstance);
        contr?.chatWidget?.focus();
    }
});
registerActiveXtermAction({
    id: "workbench.action.terminal.chat.discard" ,
    title: ( localize2WithPath(_moduleId, 4, 'Discard')),
    metadata: {
        description: ( localize2WithPath(
            _moduleId,
            5,
            'Discards the terminal current chat response, hide the chat widget, and clear the chat input.'
        ))
    },
    icon: Codicon.discard,
    menu: {
        id: MENU_TERMINAL_CHAT_WIDGET_STATUS,
        group: '0_main',
        order: 2,
        when: ( (ContextKeyExpr.and(
            TerminalChatContextKeys.focused,
            TerminalChatContextKeys.responseContainsCodeBlock
        )))
    },
    f1: true,
    precondition: ( (ContextKeyExpr.and( (ContextKeyExpr.or(
        TerminalContextKeys.processSupported,
        TerminalContextKeys.terminalHasBeenCreated
    )), TerminalChatContextKeys.focused, TerminalChatContextKeys.responseContainsCodeBlock))),
    run: (_xterm, _accessor, activeInstance) => {
        if (isDetachedTerminalInstance(activeInstance)) {
            return;
        }
        const contr = TerminalChatController.activeChatWidget || TerminalChatController.get(activeInstance);
        contr?.clear();
    }
});
registerActiveXtermAction({
    id: "workbench.action.terminal.chat.runCommand" ,
    title: ( localize2WithPath(_moduleId, 6, 'Run Chat Command')),
    shortTitle: ( localize2WithPath(_moduleId, 7, 'Run')),
    precondition: ( (ContextKeyExpr.and( (ContextKeyExpr.or(
        TerminalContextKeys.processSupported,
        TerminalContextKeys.terminalHasBeenCreated
    )),  (TerminalChatContextKeys.requestActive.negate()), TerminalChatContextKeys.agentRegistered, TerminalChatContextKeys.responseContainsCodeBlock,  (TerminalChatContextKeys.responseContainsMultipleCodeBlocks.negate())))),
    icon: Codicon.play,
    keybinding: {
        when: ( (TerminalChatContextKeys.requestActive.negate())),
        weight: 200 ,
        primary: 2048  | 3 ,
    },
    menu: {
        id: MENU_TERMINAL_CHAT_WIDGET_STATUS,
        group: '0_main',
        order: 0,
        when: ( (ContextKeyExpr.and(
            TerminalChatContextKeys.responseContainsCodeBlock,
             (TerminalChatContextKeys.responseContainsMultipleCodeBlocks.negate()),
             (TerminalChatContextKeys.requestActive.negate())
        )))
    },
    run: (_xterm, _accessor, activeInstance) => {
        if (isDetachedTerminalInstance(activeInstance)) {
            return;
        }
        const contr = TerminalChatController.activeChatWidget || TerminalChatController.get(activeInstance);
        contr?.acceptCommand(true);
    }
});
registerActiveXtermAction({
    id: "workbench.action.terminal.chat.runFirstCommand" ,
    title: ( localize2WithPath(_moduleId, 8, 'Run First Chat Command')),
    shortTitle: ( localize2WithPath(_moduleId, 9, 'Run First')),
    precondition: ( (ContextKeyExpr.and( (ContextKeyExpr.or(
        TerminalContextKeys.processSupported,
        TerminalContextKeys.terminalHasBeenCreated
    )),  (TerminalChatContextKeys.requestActive.negate()), TerminalChatContextKeys.agentRegistered, TerminalChatContextKeys.responseContainsMultipleCodeBlocks))),
    icon: Codicon.play,
    keybinding: {
        when: ( (TerminalChatContextKeys.requestActive.negate())),
        weight: 200 ,
        primary: 2048  | 3 ,
    },
    menu: {
        id: MENU_TERMINAL_CHAT_WIDGET_STATUS,
        group: '0_main',
        order: 0,
        when: ( (ContextKeyExpr.and(
            TerminalChatContextKeys.responseContainsMultipleCodeBlocks,
             (TerminalChatContextKeys.requestActive.negate())
        )))
    },
    run: (_xterm, _accessor, activeInstance) => {
        if (isDetachedTerminalInstance(activeInstance)) {
            return;
        }
        const contr = TerminalChatController.activeChatWidget || TerminalChatController.get(activeInstance);
        contr?.acceptCommand(true);
    }
});
registerActiveXtermAction({
    id: "workbench.action.terminal.chat.insertCommand" ,
    title: ( localize2WithPath(_moduleId, 10, 'Insert Chat Command')),
    shortTitle: ( localize2WithPath(_moduleId, 11, 'Insert')),
    precondition: ( (ContextKeyExpr.and( (ContextKeyExpr.or(
        TerminalContextKeys.processSupported,
        TerminalContextKeys.terminalHasBeenCreated
    )),  (TerminalChatContextKeys.requestActive.negate()), TerminalChatContextKeys.agentRegistered, TerminalChatContextKeys.responseContainsCodeBlock,  (TerminalChatContextKeys.responseContainsMultipleCodeBlocks.negate())))),
    keybinding: {
        when: ( (TerminalChatContextKeys.requestActive.negate())),
        weight: 200 ,
        primary: 512  | 3 ,
        secondary: [2048  | 3  | 512 ]
    },
    menu: {
        id: MENU_TERMINAL_CHAT_WIDGET_STATUS,
        group: '0_main',
        order: 1,
        when: ( (ContextKeyExpr.and(
            TerminalChatContextKeys.responseContainsCodeBlock,
             (TerminalChatContextKeys.responseContainsMultipleCodeBlocks.negate()),
             (TerminalChatContextKeys.requestActive.negate())
        )))
    },
    run: (_xterm, _accessor, activeInstance) => {
        if (isDetachedTerminalInstance(activeInstance)) {
            return;
        }
        const contr = TerminalChatController.activeChatWidget || TerminalChatController.get(activeInstance);
        contr?.acceptCommand(false);
    }
});
registerActiveXtermAction({
    id: "workbench.action.terminal.chat.insertFirstCommand" ,
    title: ( localize2WithPath(_moduleId, 12, 'Insert First Chat Command')),
    shortTitle: ( localize2WithPath(_moduleId, 13, 'Insert First')),
    precondition: ( (ContextKeyExpr.and( (ContextKeyExpr.or(
        TerminalContextKeys.processSupported,
        TerminalContextKeys.terminalHasBeenCreated
    )),  (TerminalChatContextKeys.requestActive.negate()), TerminalChatContextKeys.agentRegistered, TerminalChatContextKeys.responseContainsMultipleCodeBlocks))),
    keybinding: {
        when: ( (TerminalChatContextKeys.requestActive.negate())),
        weight: 200 ,
        primary: 512  | 3 ,
        secondary: [2048  | 3  | 512 ]
    },
    menu: {
        id: MENU_TERMINAL_CHAT_WIDGET_STATUS,
        group: '0_main',
        order: 1,
        when: ( (ContextKeyExpr.and(
            TerminalChatContextKeys.responseContainsMultipleCodeBlocks,
             (TerminalChatContextKeys.requestActive.negate())
        )))
    },
    run: (_xterm, _accessor, activeInstance) => {
        if (isDetachedTerminalInstance(activeInstance)) {
            return;
        }
        const contr = TerminalChatController.activeChatWidget || TerminalChatController.get(activeInstance);
        contr?.acceptCommand(false);
    }
});
registerActiveXtermAction({
    id: "workbench.action.terminal.chat.viewInChat" ,
    title: ( localize2WithPath(_moduleId, 14, 'View in Chat')),
    precondition: ( (ContextKeyExpr.and( (ContextKeyExpr.or(
        TerminalContextKeys.processSupported,
        TerminalContextKeys.terminalHasBeenCreated
    )),  (TerminalChatContextKeys.requestActive.negate()), TerminalChatContextKeys.agentRegistered))),
    icon: Codicon.commentDiscussion,
    menu: [{
            id: MENU_TERMINAL_CHAT_WIDGET_STATUS,
            group: '0_main',
            order: 1,
            when: ( (ContextKeyExpr.and(
                 (TerminalChatContextKeys.responseContainsCodeBlock.negate()),
                 (TerminalChatContextKeys.requestActive.negate())
            ))),
        },
        {
            id: MENU_TERMINAL_CHAT_WIDGET,
            group: 'navigation',
            order: 1,
            when: ( (ContextKeyExpr.and(
                 (CTX_INLINE_CHAT_EMPTY.negate()),
                TerminalChatContextKeys.responseContainsCodeBlock,
                 (TerminalChatContextKeys.requestActive.negate())
            ))),
        }],
    run: (_xterm, _accessor, activeInstance) => {
        if (isDetachedTerminalInstance(activeInstance)) {
            return;
        }
        const contr = TerminalChatController.activeChatWidget || TerminalChatController.get(activeInstance);
        contr?.viewInChat();
    }
});
registerActiveXtermAction({
    id: "workbench.action.terminal.chat.makeRequest" ,
    title: ( localize2WithPath(_moduleId, 15, 'Make Chat Request')),
    precondition: ( (ContextKeyExpr.and( (ContextKeyExpr.or(
        TerminalContextKeys.processSupported,
        TerminalContextKeys.terminalHasBeenCreated
    )),  (TerminalChatContextKeys.requestActive.negate()), TerminalChatContextKeys.agentRegistered,  (CTX_INLINE_CHAT_EMPTY.negate())))),
    icon: Codicon.send,
    keybinding: {
        when: ( (ContextKeyExpr.and(
            CTX_INLINE_CHAT_FOCUSED,
             (TerminalChatContextKeys.requestActive.negate())
        ))),
        weight: 200 ,
        primary: 3
    },
    menu: {
        id: MENU_TERMINAL_CHAT_INPUT,
        group: 'navigation',
        order: 1,
        when: ( (TerminalChatContextKeys.requestActive.negate())),
    },
    run: (_xterm, _accessor, activeInstance) => {
        if (isDetachedTerminalInstance(activeInstance)) {
            return;
        }
        const contr = TerminalChatController.activeChatWidget || TerminalChatController.get(activeInstance);
        contr?.acceptInput();
    }
});
registerActiveXtermAction({
    id: "workbench.action.terminal.chat.cancel" ,
    title: ( localize2WithPath(_moduleId, 16, 'Cancel Chat')),
    precondition: ( (ContextKeyExpr.and(
        TerminalChatContextKeys.requestActive,
        TerminalChatContextKeys.agentRegistered
    ))),
    icon: Codicon.debugStop,
    menu: {
        id: MENU_TERMINAL_CHAT_INPUT,
        group: 'navigation',
        when: TerminalChatContextKeys.requestActive,
    },
    run: (_xterm, _accessor, activeInstance) => {
        if (isDetachedTerminalInstance(activeInstance)) {
            return;
        }
        const contr = TerminalChatController.activeChatWidget || TerminalChatController.get(activeInstance);
        contr?.cancel();
    }
});
registerActiveXtermAction({
    id: "workbench.action.terminal.chat.feedbackReportIssue" ,
    title: ( localize2WithPath(_moduleId, 17, 'Report Issue')),
    precondition: ( (ContextKeyExpr.and(
         (TerminalChatContextKeys.requestActive.negate()),
         (TerminalChatContextKeys.responseContainsCodeBlock.notEqualsTo(undefined)),
        TerminalChatContextKeys.responseSupportsIssueReporting
    ))),
    icon: Codicon.report,
    menu: [{
            id: MENU_TERMINAL_CHAT_WIDGET_FEEDBACK,
            when: ( (ContextKeyExpr.and(
                 (TerminalChatContextKeys.responseContainsCodeBlock.notEqualsTo(undefined)),
                TerminalChatContextKeys.responseSupportsIssueReporting
            ))),
            group: 'inline',
            order: 3
        }],
    run: (_xterm, _accessor, activeInstance) => {
        if (isDetachedTerminalInstance(activeInstance)) {
            return;
        }
        const contr = TerminalChatController.activeChatWidget || TerminalChatController.get(activeInstance);
        contr?.acceptFeedback();
    }
});
